/**
 * Publisher payout overrides — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the backend route at
 *   /api/v1/publisher-payout-overrides
 * (see apps/api/src/routes/publisher-payout-overrides.ts).
 *
 * Raw `fetch()` is confined to this file per the dash contract (rule D6).
 * Pure helpers (formatters, validators) are unit-testable in isolation.
 */

// ---------------------------------------------------------------------------
// Fetch envelope — mirrors the `{error: string}` contract used by the API.
// ---------------------------------------------------------------------------

const API_BASE = "/api/v1";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const ct = res.headers.get("content-type") ?? "";
	const isJson = ct.includes("application/json");
	const body = isJson ? await res.json().catch(() => ({})) : null;
	if (!res.ok) {
		const msg =
			(body && typeof body === "object" && "error" in body
				? String((body as { error: unknown }).error)
				: null) ?? res.statusText ?? `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return (body ?? ({} as T)) as T;
}

function readTenantFromStorage(): string | null {
	try {
		if (
			typeof globalThis !== "undefined" &&
			typeof (globalThis as { localStorage?: Storage }).localStorage !==
				"undefined"
		) {
			const ls = (globalThis as { localStorage?: Storage }).localStorage;
			if (ls && typeof ls.getItem === "function") {
				return ls.getItem("tenant");
			}
		}
	} catch {
		// jsdom-less or sandboxed env — fall through to the default tenant.
	}
	return null;
}

function authHeaders(extra?: Record<string, string>): HeadersInit {
	return {
		accept: "application/json",
		"x-tenant-id": readTenantFromStorage() || "demo",
		...(extra ?? {}),
	};
}

// ---------------------------------------------------------------------------
// Types — mirror the backend row shape. The backend stores rate_pct as
// integer basis points (0-10000, 1bp=0.01%) so amount and rate are both
// plain integers end-to-end.
// ---------------------------------------------------------------------------

export type PayoutType = "cpa" | "cps" | "revshare";

export const PAYOUT_TYPES: readonly PayoutType[] = [
	"cpa",
	"cps",
	"revshare",
] as const;

export function isPayoutType(v: unknown): v is PayoutType {
	return typeof v === "string" && (PAYOUT_TYPES as readonly string[]).includes(v);
}

export interface OverrideRow {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	offer_id: string | null;
	payout_type: PayoutType;
	amount_cents: number | null;
	rate_pct: number | null;
	currency: string;
	created_at: number;
}

export interface ListOverridesResponse {
	affiliate_id: string;
	overrides: OverrideRow[];
}

export interface CreateOverrideInput {
	affiliate_id: string;
	offer_id?: string | null;
	payout_type: PayoutType;
	amount_cents?: number | null;
	rate_pct?: number | null;
	currency: string;
}

export interface UpdateOverrideInput {
	payout_type: PayoutType;
	amount_cents?: number | null;
	rate_pct?: number | null;
	currency: string;
}

// ---------------------------------------------------------------------------
// Presentation helpers — pure, unit-tested.
// ---------------------------------------------------------------------------

/**
 * Format a payout row for display. Returns a readable string like
 *   "USD 5.00 per action"
 *   "15.00% revshare"
 * regardless of payout type.
 */
export function formatPayout(row: OverrideRow): string {
	if (row.payout_type === "revshare") {
		return `${formatRatePct(row.rate_pct ?? 0)} revshare`;
	}
	const unit = row.payout_type === "cpa" ? "per action" : "per sale";
	return `${row.currency} ${formatCents(row.amount_cents ?? 0)} ${unit}`;
}

export function formatCents(cents: number): string {
	if (!Number.isFinite(cents)) return "0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(Math.trunc(cents));
	const whole = Math.floor(abs / 100);
	const frac = abs % 100;
	return `${sign}${whole.toLocaleString("en-US")}.${frac
		.toString()
		.padStart(2, "0")}`;
}

export function formatRatePct(basisPoints: number): string {
	if (!Number.isFinite(basisPoints)) return "0.00%";
	const pct = Math.trunc(basisPoints) / 100;
	return `${pct.toFixed(2)}%`;
}

export function formatScope(row: OverrideRow): string {
	return row.offer_id ? `Offer: ${row.offer_id}` : "All offers (global)";
}

export function formatCreatedAt(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

// ---------------------------------------------------------------------------
// Form validation — mirrors the server contract so we can give the operator
// inline feedback before the network round-trip.
// ---------------------------------------------------------------------------

export interface DraftOverride {
	affiliate_id: string;
	offer_id: string;
	payout_type: PayoutType;
	amount_display: string; // decimal entered by operator, e.g. "5.00"
	rate_pct_display: string; // percent, e.g. "15"
	currency: string;
}

export function emptyDraft(affiliateId = ""): DraftOverride {
	return {
		affiliate_id: affiliateId,
		offer_id: "",
		payout_type: "cpa",
		amount_display: "",
		rate_pct_display: "",
		currency: "USD",
	};
}

/**
 * Parse a decimal dollar string ("5.00") into integer cents. Returns null on
 * invalid input. Accepts bare integers ("5") and 0–2 decimal places.
 */
export function parseAmountToCents(display: string): number | null {
	const s = display.trim();
	if (!s) return null;
	if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
	const [wholeRaw, fracRaw] = s.split(".");
	const whole = Number.parseInt(wholeRaw ?? "0", 10);
	if (!Number.isFinite(whole)) return null;
	const frac = fracRaw ? Number.parseInt(fracRaw.padEnd(2, "0"), 10) : 0;
	const cents = whole * 100 + frac;
	if (cents < 0 || cents > 1_000_000_000) return null;
	return cents;
}

/**
 * Parse a percent string like "15" or "15.25" into integer basis points
 * (1bp = 0.01%). Rejects NaN, negatives, and values > 100%.
 */
export function parsePercentToBasisPoints(display: string): number | null {
	const s = display.trim();
	if (!s) return null;
	if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
	const n = Number.parseFloat(s);
	if (!Number.isFinite(n)) return null;
	const bp = Math.round(n * 100);
	if (bp < 0 || bp > 10_000) return null;
	return bp;
}

export interface DraftValidation {
	ok: boolean;
	error: string | null;
	payload?: CreateOverrideInput;
}

export function validateDraft(draft: DraftOverride): DraftValidation {
	if (!draft.affiliate_id.trim()) {
		return { ok: false, error: "Pick an affiliate first." };
	}
	const currency = draft.currency.trim().toUpperCase();
	if (!/^[A-Z]{3}$/.test(currency)) {
		return { ok: false, error: "Currency must be a 3-letter ISO code." };
	}
	const offerId = draft.offer_id.trim();
	if (offerId && !/^[a-z0-9_-]{1,64}$/i.test(offerId)) {
		return { ok: false, error: "Offer id has unsupported characters." };
	}
	if (draft.payout_type === "revshare") {
		const bp = parsePercentToBasisPoints(draft.rate_pct_display);
		if (bp === null) {
			return { ok: false, error: "Enter a percent between 0 and 100." };
		}
		return {
			ok: true,
			error: null,
			payload: {
				affiliate_id: draft.affiliate_id.trim(),
				offer_id: offerId || null,
				payout_type: "revshare",
				rate_pct: bp,
				currency,
			},
		};
	}
	const cents = parseAmountToCents(draft.amount_display);
	if (cents === null) {
		return { ok: false, error: "Enter a valid amount in dollars." };
	}
	return {
		ok: true,
		error: null,
		payload: {
			affiliate_id: draft.affiliate_id.trim(),
			offer_id: offerId || null,
			payout_type: draft.payout_type,
			amount_cents: cents,
			currency,
		},
	};
}

// ---------------------------------------------------------------------------
// Remote bindings.
// ---------------------------------------------------------------------------

const BASE = `${API_BASE}/publisher-payout-overrides`;

export async function listOverrides(
	affiliateId: string,
): Promise<ListOverridesResponse> {
	const res = await fetch(
		`${BASE}/list/${encodeURIComponent(affiliateId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: authHeaders(),
		},
	);
	return parseOrThrow<ListOverridesResponse>(res);
}

export async function createOverride(
	input: CreateOverrideInput,
): Promise<{ override: OverrideRow }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: authHeaders({ "content-type": "application/json" }),
		body: JSON.stringify(input),
	});
	return parseOrThrow<{ override: OverrideRow }>(res);
}

export async function updateOverride(
	id: string,
	input: UpdateOverrideInput,
): Promise<{ override: OverrideRow }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PUT",
		credentials: "include",
		headers: authHeaders({ "content-type": "application/json" }),
		body: JSON.stringify(input),
	});
	return parseOrThrow<{ override: OverrideRow }>(res);
}

export async function deleteOverride(
	id: string,
): Promise<{ deleted: boolean; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: authHeaders(),
	});
	return parseOrThrow<{ deleted: boolean; id: string }>(res);
}

// ---------------------------------------------------------------------------
// Query keys.
// ---------------------------------------------------------------------------

export const overrideQueryKeys = {
	all: ["publisher-payout-overrides"] as const,
	list: (affiliateId: string) =>
		[...overrideQueryKeys.all, "list", affiliateId] as const,
};

// ---------------------------------------------------------------------------
// Row-status taxonomy (derived, client-side only).
//
// The backend does not persist effective-date ranges; we derive a scope tag
// (global vs offer-scoped) and an age tag (recent vs aged) for UI badges and
// toolbar filter chips. "Recent" means the override was created within the
// last 7 days — a heuristic that flags freshly-tuned rules for auditors.
// ---------------------------------------------------------------------------

export type OverrideScope = "global" | "offer-scoped";
export type OverrideAge = "recent" | "aged";

export const SEVEN_DAYS_SECONDS = 7 * 24 * 60 * 60;

export function computeScope(row: OverrideRow): OverrideScope {
	return row.offer_id ? "offer-scoped" : "global";
}

export function computeAge(row: OverrideRow, now: number = Date.now() / 1000): OverrideAge {
	if (!Number.isFinite(row.created_at) || row.created_at <= 0) return "aged";
	return now - row.created_at <= SEVEN_DAYS_SECONDS ? "recent" : "aged";
}

export function describeScope(scope: OverrideScope): string {
	return scope === "offer-scoped" ? "Offer-scoped" : "Global";
}

export function describeAge(age: OverrideAge): string {
	return age === "recent" ? "Recent" : "Active";
}

// ---------------------------------------------------------------------------
// Filter utility — applied client-side after the per-affiliate fetch.
// ---------------------------------------------------------------------------

export interface OverrideFilters {
	offerQuery?: string;
	scope?: OverrideScope | "all";
	age?: OverrideAge | "all";
}

export function applyFilters(
	rows: readonly OverrideRow[],
	filters: OverrideFilters,
	now: number = Date.now() / 1000,
): OverrideRow[] {
	const offerQuery = (filters.offerQuery ?? "").trim().toLowerCase();
	const scope = filters.scope ?? "all";
	const age = filters.age ?? "all";
	return rows.filter((row) => {
		if (offerQuery) {
			const haystack = (row.offer_id ?? "").toLowerCase();
			if (!haystack.includes(offerQuery)) return false;
		}
		if (scope !== "all" && computeScope(row) !== scope) return false;
		if (age !== "all" && computeAge(row, now) !== age) return false;
		return true;
	});
}

export function describePayoutType(t: PayoutType): string {
	if (t === "cpa") return "CPA";
	if (t === "cps") return "CPS";
	return "Revshare";
}
